export type TypedEventListener<M, T extends keyof M> = (e: M[T]) => void;

export type TypedEventListenerObject<M, T extends keyof M> = { handleEvent(e: M[T]): void };

export type ValueIsCustomEvent<T> = {
  [key in keyof T]: CustomEvent;
};

export type CustomEventDetail<M, T extends keyof M> = M[T] extends CustomEvent<infer P> ? P : never;

/**
 * Just like `EventTarget`, but will dispatch CustomEvent instances with type-safety for everything (i.e. dispatch,
 * handlers).
 */
export interface TypedEventTarget<M extends ValueIsCustomEvent<M>> extends EventTarget {
  addEventListener<T extends keyof M & string>(
    type: T,
    listener: TypedEventListener<M, T> | TypedEventListenerObject<M, T> | null,
    options?: AddEventListenerOptions | boolean
  ): void;

  removeEventListener<T extends keyof M & string>(
    type: T,
    listener: TypedEventListener<M, T> | TypedEventListenerObject<M, T> | null,
    options?: EventListenerOptions | boolean
  ): void;

  dispatchCustomEvent<T extends keyof M & string>(type: T, payload: CustomEventDetail<M, T>): void;
}

// Need to allow this or you don't get the generic add/remove listeners from the interface in Typescript
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class TypedEventTarget<M extends ValueIsCustomEvent<M>> extends EventTarget implements TypedEventTarget<M> {
  dispatchCustomEvent<T extends keyof M & string>(type: T, detail: CustomEventDetail<M, T>): void {
    const e = new CustomEvent(type, { detail });
    this.dispatchEvent(e);
  }
}
